import { CalculationsDataExt, FieldDataExt } from '@castoredc/castor_prose';

import { createSlice } from '@reduxjs/toolkit';
import { updateFieldData } from 'features/questionnaires/logic/fieldsData';
import type {
  FieldData,
  Field,
  FieldValue,
  QuestionnairePresentation,
  RenderContext,
  Section,
} from '@castoredc/castor_prose';
import type { DbTask, DbCalculationsData } from '@hub/common';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { ErrorData } from 'features/error-handling/types/ErrorData';
import type {
  QuestionnaireI18nKeys,
  ProseRenderOutput,
  ProsePostQuestionnaireOutput,
} from 'features/questionnaires/services/proseService';

export type QuestionnaireState = {
  docData?: DbTask;
  readyToReceiveSnapshots: boolean;
  i18nKeys?: QuestionnaireI18nKeys;
  i18nBaseKey?: string;
  presentation: QuestionnairePresentation | undefined;
  fieldsDefinitions: Field[];
  pages: Section[];
  pageIndex: number;
  lastLoadedFieldsData: FieldData;
  lastLoadedContextData: RenderContext | null;
  lastLoadedTouchedFields: string[];
  lastLoadedCalculationsData: DbCalculationsData;
  errorData?: ErrorData;
  loading?: boolean | 'redirecting';
  resumeInFlight: boolean;
  phase: 'questionnaire' | 'postQuestionnaire';
};

export type QuestionnaireStateWithDocData = Required<
  Pick<QuestionnaireState, 'docData'>
> &
  Omit<QuestionnaireState, 'docData'>;

const initialState: QuestionnaireState = {
  readyToReceiveSnapshots: false,
  presentation: undefined,
  fieldsDefinitions: [],
  pages: [],
  pageIndex: 0,
  lastLoadedFieldsData: FieldDataExt.new(),
  lastLoadedContextData: null,
  lastLoadedTouchedFields: [],
  lastLoadedCalculationsData: CalculationsDataExt.new(),
  loading: false,
  resumeInFlight: false,
  phase: 'questionnaire' as const,
};

function assertDocDataAvailability(
  state: QuestionnaireState
): asserts state is QuestionnaireStateWithDocData {
  if (!state.docData) {
    throw new Error(
      '[ASSERT FAILED, REDUX ACTION] Attempt to rely on questionnaire.docData while it is not available in store'
    );
  }
}

const applyProseOutputToState = (
  state: QuestionnaireStateWithDocData,
  output: ProseRenderOutput
) => {
  state.docData.fieldsData = output.fieldsData;
  state.docData.contextData = output.contextData;
  state.docData.calculationsData = output.calculationsData;
  state.i18nBaseKey = output.i18nBaseKey;
  state.i18nKeys = output.i18nKeys;
  state.presentation = output.presentation;
  state.fieldsDefinitions = output.fieldsDefinitions;
  state.pages = output.fieldsSections;
};

// TODO: [Refactor] Less actions (switch from setter to event style)
const questionnaireSlice = createSlice({
  name: 'questionnaire',
  initialState,
  reducers: {
    restoreInitialState: () => initialState,
    documentLoaded: (state, action: PayloadAction<DbTask>) => {
      state.docData = action.payload;
    },
    initializationDone: state => {
      state.readyToReceiveSnapshots = true;
    },
    applyProseOutput: (state, action: PayloadAction<ProseRenderOutput>) => {
      assertDocDataAvailability(state);
      applyProseOutputToState(state, action.payload);
    },
    setTouchedFields: (state, action: PayloadAction<string[]>) => {
      assertDocDataAvailability(state);
      state.docData.touchedFields = action.payload;
    },
    setFieldDataFromInput: (
      state,
      action: PayloadAction<{
        fieldName: string;
        fieldValue: FieldValue | undefined;
      }>
    ) => {
      assertDocDataAvailability(state);

      state.docData.fieldsData = updateFieldData(
        state.docData.fieldsData,
        action.payload.fieldName,
        action.payload.fieldValue
      );
    },
    setLastLoaded: (
      state,
      action: PayloadAction<{
        fieldsData: FieldData;
        contextData: RenderContext | null;
        touchedFields: string[];
        calculationsData: DbCalculationsData;
      }>
    ) => {
      state.lastLoadedFieldsData = action.payload.fieldsData;
      state.lastLoadedContextData = action.payload.contextData;
      state.lastLoadedTouchedFields = action.payload.touchedFields;
      state.lastLoadedCalculationsData = action.payload.calculationsData;

      // Sync PROSE context to docData so that subsequent thunk reads see the latest
      // snapshot data (e.g. cross-window sync). Treat context as opaque — sync the
      // whole object without accessing individual fields.
      if (state.docData && action.payload.contextData) {
        state.docData.contextData = action.payload.contextData;
      }
    },
    goToPreviousPage: state => {
      state.pageIndex -= 1;
    },
    goToNextPage: state => {
      state.pageIndex += 1;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    setShowSummary: (state, action: PayloadAction<boolean>) => {
      assertDocDataAvailability(state);
      state.docData.showSummary = action.payload;
    },
    errorOccurred: (state, action: PayloadAction<ErrorData>) => {
      state.errorData = action.payload;
      state.readyToReceiveSnapshots = false;
    },
    setLoading: (state, action: PayloadAction<boolean | 'redirecting'>) => {
      state.loading = action.payload;
    },
    resumeSubmissionStarted: state => {
      state.resumeInFlight = true;
    },
    enterPostQuestionnaire: (
      state,
      action: PayloadAction<ProsePostQuestionnaireOutput>
    ) => {
      assertDocDataAvailability(state);
      state.phase = 'postQuestionnaire';
      state.pageIndex = 0;
      state.docData.showSummary = false;
      applyProseOutputToState(state, action.payload);
    },
  },
});

export const {
  restoreInitialState,
  documentLoaded,
  initializationDone,
  applyProseOutput,
  setFieldDataFromInput,
  setTouchedFields,
  setLastLoaded,
  goToPreviousPage,
  goToNextPage,
  setPageIndex,
  setShowSummary,
  errorOccurred,
  setLoading,
  resumeSubmissionStarted,
  enterPostQuestionnaire,
} = questionnaireSlice.actions;

export const questionnaireReducer = questionnaireSlice.reducer;
